import {
    Box,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useEffect, useState} from "react";
import {getArboxCollections, getCompanyBreakdown} from "../services/DashboatrdService";
import Loader from "../componenets/Loader";
import CompanyBreakdown from "../componenets/dashboard/CompanyBreakdown";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {useAuth} from "../contexts/AuthContext";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {RTL} from "../cache/CacheRtl";
import CoachDashboard from "./CoachDashboard";
import CompanyImpersonatePanel from "../componenets/dashboard/CompanyImpersonatePanel";

// The super-admin dashboard: a read-only all-companies overview backed by the system
// company-breakdown endpoint (system/statistics/company-breakdown). A super-admin has NO company
// context of their own — clicking a company drills into its Admins/Trainers so the super-admin can
// IMPERSONATE one of them (that's how they act inside a company; there is no "borrow the company's
// dashboard" drill-in anymore).
const CompanyOverview = ({isMobile}) => {
    const [breakdownStats, setBreakdownStats] = useState(null);
    const [arboxCollections, setArboxCollections] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null); // {id, name} → impersonate panel
    const {notificationHandler} = useMessageContext();
    const {isCompanyGroupAdmin, isSuperAdmin} = useAuth();
    // A group admin's breakdown is scoped (server-side) to only their authorized companies, so the
    // subtitle reflects "your group" rather than the super-admin's all-companies wording.
    const subtitle = isCompanyGroupAdmin() ? "סקירת החברות בקבוצה שלך" : "סקירת כלל החברות";

    useEffect(() => {
        setLoading(true);
        getCompanyBreakdown()
            .then(res => setBreakdownStats(res.data))
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Arbox collections is a SEPARATE, super-admin-only fetch, deliberately not folded into the breakdown
    // call: it goes out to a third-party API, so it must not delay (or fail) the main dashboard. A
    // failure is swallowed to a null tile rather than raising a toast — the dashboard is still useful
    // without it, and a group admin would get a 403 here by design.
    useEffect(() => {
        if (!isSuperAdmin()) return;
        getArboxCollections()
            .then(res => setArboxCollections(res.data))
            .catch(() => setArboxCollections(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The breakdown carries the company list; resolve the clicked id to {id, name} for the panel.
    const handleCompanySelect = (companyId) => {
        const list = breakdownStats?.companies || breakdownStats?.companyBreakdown || [];
        const found = list.find((c) => c.id === companyId || c.companyId === companyId);
        setSelectedCompany({
            id: companyId,
            name: found?.name || found?.companyName || "",
        });
    };

    return (
        <Box sx={{background: t.bg, minHeight: "100%"}}>
            {loading && <Loader loading={loading}/>}

            <RTL>
                <Box dir="rtl" sx={{padding: {xs: "12px", md: "16px 20px"}, display: "flex", flexDirection: "column", gap: "18px"}}>
                    {selectedCompany ? (
                        <CompanyImpersonatePanel
                            company={selectedCompany}
                            onBack={() => setSelectedCompany(null)}
                        />
                    ) : (
                        <>
                            {/* Title row (clients-page style — sits on the canvas, no white band) */}
                            <Box>
                                <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                                    דשבורד
                                </Typography>
                                <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>
                                    {subtitle}
                                </Typography>
                            </Box>

                            {breakdownStats && (
                                <CompanyBreakdown
                                    breakdownStats={breakdownStats}
                                    onCompanySelect={handleCompanySelect}
                                    isMobile={isMobile}
                                    showSubscription={!isCompanyGroupAdmin()}
                                    arboxCollections={arboxCollections}
                                />
                            )}
                        </>
                    )}
                </Box>
            </RTL>
        </Box>
    );
};

// The company-less personas — super-admin (all companies) and company group admin (only their
// authorized companies) — see the companies overview with an impersonation drill-down; the server
// scopes the same breakdown endpoint per persona. Company-scoped roles (company admin / trainer) see
// their own company's coach dashboard.
const Dashboard = () => {
    const {isSuperAdmin, isCompanyGroupAdmin} = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return (isSuperAdmin() || isCompanyGroupAdmin())
        ? <CompanyOverview isMobile={isMobile}/>
        : <CoachDashboard/>;
};

export default Dashboard;
